import { api, LightningElement, wire } from "lwc";
import { logger } from "@devforce/trailhead-client-logger";

import { EffectAdapter } from "common/effectAdapter";
import { getAssetsUrl } from "common/helpers";

type Size = "x-small" | "small" | "medium" | "large" | "xlarge" | "override";

export type Sprite =
  | "action"
  | "custom"
  | "doctype"
  | "social"
  | "social-round"
  | "trailhead"
  | "utility";

export const DEFAULT_SPRITE: Sprite = "utility";

const fetches = new Map<string, Promise<void>>();

export default class extends LightningElement {
  @api sprite: Sprite = DEFAULT_SPRITE;
  @api size: Size = "medium";
  @api symbol: string = "";

  private node: SVGSymbolElement | undefined;

  @wire(EffectAdapter, { sprite: "$sprite", symbol: "$symbol" })
  private async onChange() {
    if (this.willFetchSprite) {
      this.appendNodes().catch((e: Error) => logger.warn(e.message));
    }
  }

  private get className() {
    return `size--${this.size}`;
  }

  private get assetUrl() {
    // eslint-disable-next-line local/import-image-path
    return getAssetsUrl(`/icons/${this.sprite}-sprite/svg/symbols.svg`);
  }

  private get willFetchSprite() {
    return /^http/.test(this.assetUrl);
  }

  private get href() {
    return `${this.assetUrl}#${this.symbol}`;
  }

  private get symbolId() {
    return `tds__${this.sprite}__${this.symbol}`;
  }

  renderedCallback() {
    if (this.willFetchSprite) {
      this.appendNodes().catch((e: Error) => logger.warn(e.message));
    }
  }

  private async appendNodes() {
    let { symbolId, sprite } = this;
    if (this.node && this.node.id === symbolId) return;
    await this.fetchSprite(sprite);
    let svg = this.template.querySelector("svg")!;
    const symbol = document.getElementById(symbolId);
    if (!symbol) {
      logger.warn(`symbolId ${symbolId} was not found`);
      return;
    }

    let node = symbol.cloneNode(true) as SVGSymbolElement;
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    for (let n of Array.from(node.childNodes)) svg.appendChild(n);
    svg.setAttribute("viewBox", node.getAttribute("viewBox")!);
    this.node = node;
  }

  private async fetchSprite(sprite: Sprite) {
    if (fetches.has(sprite)) return fetches.get(sprite);
    let promise = fetch(this.assetUrl)
      .then((res) => res.text())
      .then((svg) => svg.replace(/id="/g, `id="tds__${sprite}__`))
      .then((svg) => {
        let node = document.createElement("div");
        node.innerHTML = svg;
        node.style.display = "none";
        document.body.appendChild(node);
      });
    fetches.set(sprite, promise);
    return promise;
  }
}
